import { BookOpen, ClipboardList, MessageSquare, Pencil, Settings } from 'lucide-react';
import { cn } from '../lib/utils';

export type SessionTab = 'material' | 'plan' | 'chat' | 'notes' | 'settings';

export interface SessionTabBarProps {
  activeTab: SessionTab;
  onTabChange: (tab: SessionTab) => void;
  variant?: 'horizontal' | 'vertical';
  className?: string;
}

const tabConfig: Record<
  SessionTab,
  { icon: typeof BookOpen; label: string }
> = {
  material: { icon: BookOpen, label: 'Material' },
  plan: { icon: ClipboardList, label: 'Plan' },
  chat: { icon: MessageSquare, label: 'Chat' },
  notes: { icon: Pencil, label: 'Notes' },
  settings: { icon: Settings, label: 'Settings' },
};

export const SessionTabBar = ({
  activeTab,
  onTabChange,
  variant = 'horizontal',
  className,
}: SessionTabBarProps) => {
  const isVertical = variant === 'vertical';

  return (
    <div
      className={cn(
        'flex items-center',
        isVertical ? 'flex-col gap-1' : 'gap-0',
        className
      )}
    >
      {(Object.keys(tabConfig) as SessionTab[]).map((tab) => {
        const { icon: Icon, label } = tabConfig[tab];
        const isActive = activeTab === tab;

        if (isVertical) {
          return (
            <button
              key={tab}
              type="button"
              onClick={() => onTabChange(tab)}
              className={cn(
                'flex h-[52px] w-12 flex-col items-center justify-center gap-0.5 rounded-lg transition-colors',
                isActive
                  ? 'bg-orange-500/10 text-orange-500'
                  : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
              )}
            >
              <Icon className="h-5 w-5" />
              <span className="text-[9px] font-medium">{label}</span>
            </button>
          );
        }

        return (
          <button
            key={tab}
            type="button"
            onClick={() => onTabChange(tab)}
            className={cn(
              'flex h-10 items-center gap-1.5 border-b-2 px-3.5 transition-colors',
              isActive
                ? 'border-orange-500 text-orange-500'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="text-sm font-medium">{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export interface MobileSessionTabBarProps {
  activeTab: 'material' | 'plan';
  onTabChange: (tab: 'material' | 'plan') => void;
  className?: string;
}

export const MobileSessionTabBar = ({
  activeTab,
  onTabChange,
  className,
}: MobileSessionTabBarProps) => {
  return (
    <div
      className={cn(
        'flex h-10 items-center border-b border-slate-200 bg-slate-50 px-3',
        className
      )}
    >
      <button
        type="button"
        onClick={() => onTabChange('material')}
        className={cn(
          'flex h-10 items-center gap-1.5 border-b-2 px-3.5 transition-colors',
          activeTab === 'material'
            ? 'border-orange-500 text-orange-500'
            : 'border-transparent text-slate-400 hover:text-slate-600'
        )}
      >
        <BookOpen className="h-4 w-4" />
        <span className="text-sm font-semibold">Material</span>
      </button>
      <button
        type="button"
        onClick={() => onTabChange('plan')}
        className={cn(
          'flex h-10 items-center gap-1.5 border-b-2 px-3.5 transition-colors',
          activeTab === 'plan'
            ? 'border-orange-500 text-orange-500'
            : 'border-transparent text-slate-400 hover:text-slate-600'
        )}
      >
        <ClipboardList className="h-4 w-4" />
        <span className="text-sm font-medium">Lesson Plan</span>
      </button>
    </div>
  );
};
